import { MapPin, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useGetProviderDirectoryDepartmentsQuery,
  useGetProviderDirectoryLocationsQuery,
  useGetProviderDirectoryServiceQuery,
  useUpdateProviderDirectoryServiceMutation,
} from '@/services/api/providerDirectoryV2Service';
import type {
  ProviderDirectoryV2AddressSnippet,
  ProviderDirectoryV2LocationSummary,
  ProviderDirectoryV2Service,
} from '@/services/types/provider-directory-v2-types';

import { CreateDepartmentModal } from './CreateDepartmentModal';

const cx = (...parts: Array<string | null | undefined | false>) =>
  parts.filter(Boolean).join(' ');

const formatAddressLine = (addr?: ProviderDirectoryV2AddressSnippet | null) => {
  if (!addr) return null;
  const parts = [
    addr.address_line1,
    addr.city,
    addr.state,
    addr.postal_code,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : (addr.formatted_address ?? null);
};

type Slot = 'clinical' | 'billing' | 'records';

const SlotLabel = ({ slot }: { slot: Slot }) => {
  if (slot === 'clinical') return <>Clinical location</>;
  if (slot === 'billing') return <>Billing department</>;
  return <>Records department</>;
};

const SlotHelp = ({ slot }: { slot: Slot }) => {
  if (slot === 'clinical') return <>Where care is provided.</>;
  if (slot === 'billing')
    return <>Where billing/collections requests should be routed.</>;
  return <>Where medical records requests should be routed.</>;
};

export const EditRoutingModal = ({
  open,
  onClose,
  serviceId,
}: {
  open: boolean;
  onClose: () => void;
  serviceId: string;
}) => {
  const { data: service, isFetching: isFetchingService } =
    useGetProviderDirectoryServiceQuery(
      { serviceId },
      { skip: !open || !serviceId },
    );
  const [updateService, { isLoading: isSaving }] =
    useUpdateProviderDirectoryServiceMutation();

  const [activeSlot, setActiveSlot] = useState<Slot | null>(null);
  const [createSlot, setCreateSlot] = useState<Slot | null>(null);
  const [pickerQ, setPickerQ] = useState('');
  const debouncedPickerQ = useDebouncedValue(pickerQ.trim(), 200);

  const [draftClinicalId, setDraftClinicalId] = useState<string | null>(null);
  const [draftBillingId, setDraftBillingId] = useState<string | null>(null);
  const [draftRecordsId, setDraftRecordsId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setActiveSlot(null);
    setCreateSlot(null);
    setPickerQ('');
  }, [open]);

  useEffect(() => {
    if (!open || !service) return;
    setDraftClinicalId(service.clinical_location?.id ?? null);
    setDraftBillingId(service.billing_location?.id ?? null);
    setDraftRecordsId(service.records_location?.id ?? null);
  }, [open, service]);

  const locationsQuery = useGetProviderDirectoryLocationsQuery(
    { q: debouncedPickerQ || undefined, limit: 50, offset: 0 },
    { skip: !open || !activeSlot || activeSlot !== 'clinical' },
  );
  const departmentsQuery = useGetProviderDirectoryDepartmentsQuery(
    { q: debouncedPickerQ || undefined, limit: 50, offset: 0 },
    { skip: !open || !activeSlot || activeSlot === 'clinical' },
  );

  const selectedMap = useMemo(() => {
    const byId: Record<string, ProviderDirectoryV2LocationSummary> = {};
    if (service?.clinical_location)
      byId[service.clinical_location.id] = service.clinical_location;
    if (service?.billing_location)
      byId[service.billing_location.id] = service.billing_location;
    if (service?.records_location)
      byId[service.records_location.id] = service.records_location;
    return byId;
  }, [service]);

  const draft = useMemo(() => {
    return {
      clinical: draftClinicalId,
      billing: draftBillingId,
      records: draftRecordsId,
    } satisfies Record<Slot, string | null>;
  }, [draftBillingId, draftClinicalId, draftRecordsId]);

  const isDirty = useMemo(() => {
    if (!service) return false;
    const currentClinical = service.clinical_location?.id ?? null;
    const currentBilling = service.billing_location?.id ?? null;
    const currentRecords = service.records_location?.id ?? null;
    return (
      currentClinical !== draftClinicalId ||
      currentBilling !== draftBillingId ||
      currentRecords !== draftRecordsId
    );
  }, [draftBillingId, draftClinicalId, draftRecordsId, service]);

  const pick = (slot: Slot, locationId: string | null) => {
    if (slot === 'clinical') setDraftClinicalId(locationId);
    if (slot === 'billing') setDraftBillingId(locationId);
    if (slot === 'records') setDraftRecordsId(locationId);
  };

  const closePicker = () => {
    setActiveSlot(null);
    setPickerQ('');
  };

  const handleSave = async () => {
    if (!service) return;
    if (!draftClinicalId) {
      toast.error('Clinical location is required.');
      return;
    }
    try {
      await updateService({
        serviceId: service.id,
        patch: {
          clinical_location_id: draftClinicalId,
          billing_location_id: draftBillingId,
          records_location_id: draftRecordsId,
        },
      }).unwrap();
      toast.success('Routing updated');
      onClose();
    } catch (err) {
      console.error('Failed to update routing', err);
      toast.error('Failed to update routing');
    }
  };

  const currentLocationForSlot = (
    svc: ProviderDirectoryV2Service,
    slot: Slot,
  ) => {
    if (slot === 'clinical') return svc.clinical_location ?? null;
    if (slot === 'billing') return svc.billing_location ?? null;
    return svc.records_location ?? null;
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit routing</DialogTitle>
          <DialogDescription>
            Update where requests are routed for this provider service.
          </DialogDescription>
        </DialogHeader>

        {!service ? (
          <div className="text-sm text-zinc-600">
            {isFetchingService ? 'Loading…' : 'Service not found.'}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
              <div className="text-xs text-zinc-500">Service</div>
              <div className="text-sm font-medium text-zinc-900">
                {service.name}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {(['clinical', 'billing', 'records'] as Slot[]).map((slot) => {
                const current = currentLocationForSlot(service, slot);
                const draftId = draft[slot];
                const draftLoc =
                  (draftId ? selectedMap[draftId] : null) ??
                  (draftId === current?.id ? current : null);

                return (
                  <div
                    key={slot}
                    className="rounded-xl border border-zinc-200 bg-white p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-zinc-500" />
                          <SlotLabel slot={slot} />
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          <SlotHelp slot={slot} />
                        </div>

                        <div className="mt-2">
                          <div className="text-sm font-medium text-zinc-900 truncate">
                            {draftLoc?.name ?? '—'}
                          </div>
                          <div className="text-xs text-zinc-500 truncate mt-0.5">
                            {formatAddressLine(draftLoc?.address ?? null) ??
                              (draftId ? 'Address not set' : '—')}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveSlot(slot)}
                        >
                          Change
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCreateSlot(slot);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Create
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {activeSlot ? (
              <div className="rounded-xl border border-zinc-200 bg-white p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-900">
                      Select <SlotLabel slot={activeSlot} />
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      Search and reuse an existing entry, or create a new one.
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={closePicker}>
                    Done
                  </Button>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="h-4 w-4 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <Input
                      value={pickerQ}
                      onChange={(e) => setPickerQ(e.target.value)}
                      placeholder="Search locations/departments…"
                      className="pl-8"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => pick(activeSlot, null)}
                    disabled={!draft[activeSlot]}
                  >
                    Clear
                  </Button>
                </div>

                <div className="mt-3 max-h-[320px] overflow-auto space-y-2">
                  {(
                    activeSlot === 'clinical'
                      ? locationsQuery.isFetching
                      : departmentsQuery.isFetching
                  ) ? (
                    <div className="text-xs text-zinc-500 py-2">Loading…</div>
                  ) : null}

                  {(
                    (activeSlot === 'clinical'
                      ? locationsQuery.data?.results
                      : departmentsQuery.data?.results) ?? []
                  ).map((loc) => (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => pick(activeSlot, loc.id)}
                      className={cx(
                        'w-full rounded-lg border px-3 py-2 text-left transition',
                        draft[activeSlot] === loc.id
                          ? 'border-blue-200 bg-blue-50/60'
                          : 'border-zinc-200 hover:bg-zinc-50',
                      )}
                    >
                      <div className="text-sm font-medium text-zinc-900 truncate">
                        {loc.name}
                      </div>
                      <div className="text-xs text-zinc-500 truncate mt-0.5">
                        {formatAddressLine(loc.address ?? null) ?? '—'}
                      </div>
                    </button>
                  ))}

                  {!(activeSlot === 'clinical'
                    ? locationsQuery.isFetching
                    : departmentsQuery.isFetching) &&
                  (
                    (activeSlot === 'clinical'
                      ? locationsQuery.data?.results
                      : departmentsQuery.data?.results) ?? []
                  ).length === 0 ? (
                    <div className="text-sm text-zinc-600 py-3">
                      No matches. Try a broader search.
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="flex items-center justify-between gap-3 pt-2">
              <div className="text-xs text-zinc-500">
                {isDirty
                  ? 'You have unsaved routing changes.'
                  : 'No routing changes.'}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={!isDirty || isSaving}>
                  Save routing
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {service && createSlot ? (
        <CreateDepartmentModal
          open={Boolean(createSlot)}
          onClose={() => setCreateSlot(null)}
          slot={createSlot}
          service={service}
          onCreated={(locationId) => {
            pick(createSlot, locationId);
          }}
        />
      ) : null}
    </Dialog>
  );
};
